<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import DropdownTrigger from "@/components/ui/DropdownMenu/DropdownTrigger.vue";
import SelectableList from "@/components/ui/SelectableList/SelectableList.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { ChangeTypeItem, FieldChangeName } from "@/model/change";

import { stickyChanges } from "../stickyChanges";

const { t } = useI18n();

const actions = computed<ChangeTypeItem[]>(() =>
  Object.entries(stickyChanges).map(([id, meta]) => ({
    id: id as FieldChangeName,
    name: meta.keyField,
    icon: meta.icon,
  })),
);

const model = defineModel<ChangeTypeItem[]>({ default: [] });

const selection = computed({
  get: () => model.value,
  set: (values: ChangeTypeItem[]) => (model.value = values),
});

const triggerHeader = computed(() => {
  const length = model.value.length;
  const name = model.value[0]?.name;
  return t("stickyChange.selectedItems", { item: name ? t(name) : "" }, length);
});
</script>

<template>
  <div class="change-types">
    <DropdownMenu width="md" stay-open-on-content-click>
      <template #trigger="{ isOpen }">
        <DropdownTrigger
          :color="selection.length ? 'primary' : 'grey'"
          :value="triggerHeader"
          icon="general/filter"
          :activated="isOpen"
          :aria-expanded="isOpen"
          aria-controls="filter-dropdown-menu"
          aria-haspopup="listbox"
          :aria-label="$t('stickyChange.filterByChangeType')"
        />
      </template>
      <SelectableList
        id="filter-dropdown-menu"
        v-model="selection"
        v-keyboard-nav
        :values="actions"
      >
        <template #before="{ item }">
          <SvgIcon :name="item.icon" class="objective-icon" height="16" width="16" />
        </template>
        <template #default="{ item }">
          {{ $t(item.name) }}
        </template>
      </SelectableList>
    </DropdownMenu>
  </div>
</template>

<style lang="scss">
@use "@/styles/mixins/typography";

.change-types {
  .dropdown-menu {
    .dropdown-trigger {
      padding: 5px 12px;

      @include typography.small("semi-bold");
    }
  }
}
</style>
